<template>
    <DataTable
        :fields="fields"
        :items="items"
        no-padding
        v-bind="$attrs"
        v-on="$listeners"
    >
        <template #head(avgPrice)="{label}">
            <Tooltip :text="$t('averageProductPriceFromTheSelectedTimeRange')">{{ label }}</Tooltip>
        </template>
        <template #head(minPrice)="{label}">
            <Tooltip :text="$t('minimumPriceThatOccurredInTheSelectedPeriod')">{{ label }}</Tooltip>
        </template>
        <template #head(maxPrice)="{label}">
            <Tooltip :text="$t('maximumPriceThatOccurredInTheSelectedPeriod')">{{ label }}</Tooltip>
        </template>
        <template #head(offersNumber)="{label}">
            <Tooltip :text="$t('numberOfOffersAvailableOnTheLastDayOfTheSelectedPeriod')">{{ label }}</Tooltip>
        </template>
        <template #head(rankingByPrice)="{label}">
            <Tooltip :text="$t('placeOfTheManufacturersProductCompared')">{{ label }}</Tooltip>
        </template>
        <template #head(rankingByNumberOfOffers)="{label}">
            <Tooltip :text="$t('placeOfTheManufacturerSProductComparedToCompetitiveProductsByTheNumberOfOffers')">{{ label }}</Tooltip>
        </template>
        <template #cell(rankingByPrice)="{value}">
            <span v-if="value">{{ value }}</span>
            <Tooltip v-else :text="$t('productUnavailable')">- / -</Tooltip>
        </template>
        <template #cell(rankingByNumberOfOffers)="{value}">
            <div v-if="value" class="nowrap d-flex align-items-center gap-2">
                <span class="width-min-40">{{ value }}</span>
                <RankingBar :percent="rankingPercent(value)" :bar-width="80" />
            </div>
            <Tooltip v-else :text="$t('productUnavailable')">- / -</Tooltip>
        </template>
        <template #cell(minPrice)="{item, value}">
            <Tooltip type="search" :html="dateRangesToHtml(item.minPriceOccurrences)" variant="secondary">{{ value }}</Tooltip>
        </template>
        <template #cell(maxPrice)="{item, value}">
            <Tooltip type="search" :html="dateRangesToHtml(item.maxPriceOccurrences)" variant="secondary">{{ value }}</Tooltip>
        </template>
        <template #cell(action)="{item}">
            <div v-if="actions" class="d-flex gap-1 justify-content-end">
                <ButtonIcon
                    size="sm"
                    icon="eye"
                    variant="info"
                    :disabled="isDeleting(item.reportId)"
                    :loading="false"
                    @click="showItem(item)"
                />
                <ButtonIcon
                    size="sm"
                    icon="edit-2"
                    variant="primary"
                    :disabled="isDeleting(item.reportId)"
                    :loading="false"
                    @click="editItem(item)"
                />
                <ButtonIcon
                    size="sm"
                    icon="trash-2"
                    variant="danger"
                    :disabled="false"
                    :loading="isDeleting(item.reportId)"
                    @click="deleteItem(item)"
                />
            </div>
        </template>
    </DataTable>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import RankingBar from '@/components/common/RankingBar.vue';

    export default {
        name: 'RankingTable',
        components: {
            DataTable,
            Tooltip,
            RankingBar,
        },
        props: {
            items: {
                type: Array,
                required: true,
            },
            actions: {
                type: Boolean,
                default: true,
            },
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {};
        },
        computed: {
            fields() {
                return [
                    { key: 'name', label: this.$t('name') },
                    { key: 'sku', label: this.$t('sku') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'avgPrice', label: this.$t('avgPrice'), tdClass: 'nowrap', formatter: val => this.$format.currency(val) },
                    { key: 'minPrice', label: this.$t('minPrice'), tdClass: 'nowrap', formatter: val => this.$format.currency(val) },
                    { key: 'maxPrice', label: this.$t('maxPrice'), tdClass: 'nowrap', formatter: val => this.$format.currency(val) },
                    { key: 'offersNumber', label: this.$t('offersNumber') },
                    { key: 'rankingByPrice', label: this.$t('rankingByPrice') },
                    { key: 'rankingByNumberOfOffers', label: this.$t('rankingByNumberOfOffers') },
                    { key: 'action', label: '', tdClass: 'text-right', thClass: 'text-right' },
                ];
            },
        },
        methods: {
            dateRangesToHtml(data) {
                let htmlData = Object.entries(data)
                    .map(
                        ([key, ranges]) => `
                            <div>
                                <strong>${key}</strong>
                                <ul class="pl-3 m-0">
                                    ${ranges.map(range => `<li>${this.$format.date(range.start)} - ${this.$format.date(range.end)}</li>`).join('')}
                                </ul>
                            </div>
                        `
                    )
                    .join('');
                if (!htmlData) htmlData = this.$t('noData');
                return `<div class="d-flex flex-column justify-content-start gap-2 text-left">${htmlData}</div>`;
            },
            showItem(item) {
                return this.parentComponent.showItem(item);
            },
            editItem(item) {
                return this.parentComponent.editItem(item);
            },
            deleteItem(item) {
                return this.parentComponent.deleteItem(item);
            },
            isDeleting(id) {
                return this.parentComponent.requestCompetitionReportSet.getLoading(`delete-${id}`);
            },
            parseRanking(value) {
                if (!value) return null;
                const [rankStr, totalStr] = value.split('/').map(item => item.trim());
                const rank = parseInt(rankStr, 10);
                const total = parseInt(totalStr, 10);
                if (!rank || !total || total <= 0) {
                    return null;
                }
                return { rank, total };
            },
            rankingPercent(value) {
                const parsed = this.parseRanking(value);
                if (!parsed) return 0;
                const { rank, total } = parsed;
                if (rank < 1 || rank > total) return 0;
                // 1 = najlepsze miejsce -> 100%
                // total = najgorsze miejsce -> 1/total
                const percent = ((total - rank + 1) / total) * 100;
                return Math.round(percent);
            },
        },
    };
</script>
